import { useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { isApiError } from '@dorixona/api'
import { formatDayTime, formatTime, nowMs } from '@dorixona/utils'
import { DetailHead } from '../../ui/DetailHead'
import { Icon } from '../../ui/Icon'
import { Failed, Loading, failureText } from '../../ui/Load'
import { Modal } from '../../ui/Modal'
import { Select, type SelectOption } from '../../ui/Select'
import { useUnsavedWarning } from '../../ui/useUnsavedWarning'
import { initials } from '../auth/model'
import { useSession } from '../auth/session'
import { useRoles } from '../roles/api'
import { useStores } from '../stores/api'
import { storeTitle } from '../stores/model'
import {
  useDeleteStaff,
  useResetPassword,
  useSaveStaff,
  useStaffMember,
  type SavedStaff,
  type Staff,
} from './api'
import { IssuedPassword } from './IssuedPassword'
import { staffFailure } from './model'
import { StaffSessions } from './StaffSessions'
import { listAddress } from '../../app/listMemory'

export interface StaffScreenProps {
  onToast: (message: string) => void
}

/** Пароль нового сотрудника едет на его карточку в состоянии перехода. */
interface IssuedState {
  issued?: { name: string; password: string }
}

/**
 * Карточка сотрудника — отдельной страницей (`/admins/:id`, новый —
 * `/admins/new`), а не окном: в ней форма, доступ и сеансы, им тесно в окне.
 */
export function StaffScreen({ onToast }: StaffScreenProps) {
  const navigate = useNavigate()
  const params = useParams()
  const isNew = params.id === 'new'
  const id = isNew ? 0 : Number(params.id)
  const member = useStaffMember(id)

  if (!isNew && member.isPending) return <Loading what="сотрудника" />
  if (!isNew && member.isError) {
    if (isApiError(member.error) && member.error.code === 'not_found') {
      return (
        <div className="ui-card">
          <div className="ui-empty">
            <Icon name="i-badge" />
            <b>Сотрудник не найден</b>
            <p>Возможно, его удалили.</p>
            <button
              type="button"
              className="ui-btn"
              data-variant="secondary"
              onClick={() => {
                void navigate(listAddress('/admins'))
              }}
            >
              К списку сотрудников
            </button>
          </div>
        </div>
      )
    }
    return (
      <Failed
        what="сотрудника"
        error={member.error}
        onRetry={() => {
          void member.refetch()
        }}
      />
    )
  }

  // Ключ — сотрудник: другая карточка начинается с чистого черновика.
  return <StaffCard key={id} person={isNew ? null : (member.data as Staff)} onToast={onToast} />
}

interface Draft {
  name: string
  phone: string
  email: string
  roleId: number
  storeId: number
}

const toDraft = (person: Staff | null, roleId: number): Draft => ({
  name: person?.name ?? '',
  phone: person?.phone ?? '',
  email: person?.email ?? '',
  roleId: person?.roleId ?? roleId,
  storeId: person?.storeId ?? 0,
})

interface StaffCardProps {
  /** null — новый сотрудник. */
  person: Staff | null
  onToast: (message: string) => void
}

function StaffCard({ person, onToast }: StaffCardProps) {
  const { me, can } = useSession()
  const navigate = useNavigate()
  const location = useLocation()
  const roles = useRoles()
  const stores = useStores()
  const save = useSaveStaff()
  const reset = useResetPassword()
  const remove = useDeleteStaff()
  const [edits, setEdits] = useState<Draft | null>(null)
  const [error, setError] = useState('')
  const [removing, setRemoving] = useState(false)
  const [issued, setIssued] = useState((location.state as IssuedState | null)?.issued ?? null)
  const [now] = useState(() => nowMs())

  const allRoles: SelectOption<number>[] = (roles.data?.items ?? []).map((role) => ({
    value: role.id,
    label: role.name,
  }))
  const allStores: SelectOption<number>[] = [
    { value: 0, label: 'Все аптеки' },
    ...(stores.data ?? []).map((store) => ({ value: store.id, label: storeTitle(store) })),
  ]
  // Справочник мог не прийти или не знать значение сотрудника — тогда его
  // собственное значение всё равно в списке: иначе поле показало бы чужое.
  const roleOptions =
    person && !allRoles.some((o) => o.value === person.roleId)
      ? [{ value: person.roleId, label: person.roleName }, ...allRoles]
      : allRoles
  const storeOptions =
    person && !allStores.some((o) => o.value === person.storeId)
      ? [...allStores, { value: person.storeId, label: 'аптека' }]
      : allStores

  const saved = toDraft(person, allRoles[0]?.value ?? 0)
  const draft = edits ?? saved
  const dirty = person === null || JSON.stringify(draft) !== JSON.stringify(saved)
  useUnsavedWarning(edits !== null && dirty)

  const canEdit = person ? can('admins', 'u') : can('admins', 'c')
  const self = person !== null && person.id === me?.id
  // Владельцу роль и аптеку не меняют — сервер ответил бы 409 `protected`.
  const owner = person?.system ?? false
  // Себя и владельца не отключают и не удаляют: можно остаться без доступа.
  const locked = owner || self
  const ready = draft.name.trim() !== '' && draft.phone.trim() !== '' && draft.roleId > 0

  const edit = (patch: Partial<Draft>) => {
    setEdits({ ...draft, ...patch })
  }

  function input(base: Draft, active: boolean) {
    return {
      id: person?.id ?? 0,
      name: base.name.trim(),
      phone: base.phone.trim(),
      email: base.email.trim(),
      roleId: base.roleId,
      storeId: base.storeId,
      active,
    }
  }

  function submit() {
    setError('')
    save.mutate(input(draft, person?.active ?? true), {
      onSuccess: ({ staff, temporaryPassword }: SavedStaff) => {
        setEdits(null)
        if (person) {
          onToast(`Сотрудник ${staff.name} сохранён`)
          return
        }
        // Новый — на его карточку; пароль показать там один раз.
        void navigate(`/admins/${staff.id}`, {
          replace: true,
          state: { issued: { name: staff.name, password: temporaryPassword } },
        })
      },
      onError: (failure) => {
        setError(staffFailure(failure))
      },
    })
  }

  function toggleActive(current: Staff) {
    const active = !current.active
    save.mutate(input(saved, active), {
      onSuccess: ({ staff }) => {
        onToast(
          active ? `${staff.name} снова может входить` : `${staff.name} больше не войдёт в админку`,
        )
      },
      onError: (failure) => {
        onToast(failureText(failure))
      },
    })
  }

  function resetPassword(current: Staff) {
    reset.mutate(current.id, {
      onSuccess: (password) => {
        setIssued({ name: current.name, password })
      },
      onError: (failure) => {
        onToast(failureText(failure))
      },
    })
  }

  function confirmRemove(current: Staff) {
    remove.mutate(current.id, {
      onSuccess: () => {
        onToast(`${current.name} удалён — вход в админку закрыт`)
        void navigate(listAddress('/admins'))
      },
      onError: (failure) => {
        setRemoving(false)
        onToast(failureText(failure))
      },
    })
  }

  function closeIssued() {
    setIssued(null)
    // Пароль больше не показывать: «Назад» и обновление страницы его не вернут.
    void navigate(location.pathname, { replace: true, state: null })
  }

  return (
    <>
      <DetailHead
        backLabel="Сотрудники"
        onBack={() => {
          void navigate(listAddress('/admins'))
        }}
        title={person ? person.name : 'Новый сотрудник'}
        avatar={
          person ? (
            <span className="ui-avatar stf-face" aria-hidden="true">
              {initials(person.name)}
            </span>
          ) : undefined
        }
        sub={
          person ? (
            <>
              <StaffStatus person={person} now={now} />
              <span>{person.roleName}</span>
              <span className="dot" aria-hidden="true" />
              <span>
                {person.lastLogin ? `вход ${formatDayTime(person.lastLogin)}` : 'ещё не входил'}
              </span>
              {self ? <span className="ui-tag">это вы</span> : null}
              {owner ? <span className="ui-tag">владелец</span> : null}
              {person.mustChangePassword ? (
                <span className="ui-tag" data-tone="warning">
                  пароль временный
                </span>
              ) : null}
            </>
          ) : null
        }
        actions={
          person ? (
            <>
              {!canEdit ? <span className="ui-tag">только просмотр</span> : null}
              {canEdit && !owner ? (
                <button
                  type="button"
                  className="ui-btn"
                  data-variant="secondary"
                  disabled={reset.isPending}
                  aria-busy={reset.isPending}
                  onClick={() => {
                    resetPassword(person)
                  }}
                >
                  <Icon name="i-key" />
                  Сбросить пароль
                </button>
              ) : null}
              {canEdit && !locked ? (
                <button
                  type="button"
                  className="ui-btn"
                  data-variant="secondary"
                  disabled={save.isPending}
                  onClick={() => {
                    toggleActive(person)
                  }}
                >
                  <Icon name={person.active ? 'i-exit' : 'i-check'} />
                  {person.active ? 'Отключить доступ' : 'Вернуть доступ'}
                </button>
              ) : null}
              {can('admins', 'd') && !locked ? (
                <button
                  type="button"
                  className="ui-iconbtn ui-danger-icon"
                  aria-label="Удалить сотрудника"
                  data-tip="Удалить сотрудника"
                  data-tip-pos="bottom"
                  data-tip-align="end"
                  onClick={() => {
                    setRemoving(true)
                  }}
                >
                  <Icon name="i-trash" />
                </button>
              ) : null}
            </>
          ) : undefined
        }
      />

      <div className="ui-detail-grid">
        <div className="ui-detail-col">
          <section className="ui-card" aria-labelledby="stf-data">
            <div className="ui-card-head">
              <h3 id="stf-data">Данные для входа</h3>
            </div>
            <div className="ui-card-body ui-form">
              <div className="ui-field">
                <label className="ui-label" htmlFor="stf-name">
                  Имя и фамилия
                </label>
                <input
                  id="stf-name"
                  placeholder="Например, Дилноза Азимова"
                  className="ui-input"
                  autoComplete="off"
                  disabled={!canEdit}
                  value={draft.name}
                  onChange={(e) => {
                    edit({ name: e.target.value })
                  }}
                />
              </div>
              <div className="ui-form-row">
                <div className="ui-field">
                  <label className="ui-label" htmlFor="stf-phone">
                    Телефон для входа
                  </label>
                  <input
                    id="stf-phone"
                    className="ui-input"
                    type="tel"
                    inputMode="tel"
                    placeholder="+998 90 123-45-67"
                    disabled={!canEdit}
                    value={draft.phone}
                    onChange={(e) => {
                      edit({ phone: e.target.value })
                    }}
                  />
                </div>
                <div className="ui-field">
                  <label className="ui-label" htmlFor="stf-email">
                    Рабочая почта <span className="opt">необязательно</span>
                  </label>
                  <input
                    id="stf-email"
                    placeholder="name@dorixona.uz"
                    className="ui-input"
                    type="email"
                    disabled={!canEdit}
                    value={draft.email}
                    onChange={(e) => {
                      edit({ email: e.target.value })
                    }}
                  />
                </div>
              </div>
              {error ? (
                <div className="ui-error" role="alert">
                  <Icon name="i-alert" />
                  {error}
                </div>
              ) : null}
            </div>
          </section>

          <section className="ui-card" aria-labelledby="stf-access">
            <div className="ui-card-head">
              <h3 id="stf-access">Роль и аптека</h3>
            </div>
            <div className="ui-card-body ui-form">
              <div className="ui-form-row">
                <div className="ui-field">
                  <span className="ui-label" aria-hidden="true">
                    Роль
                  </span>
                  <Select
                    label="Роль"
                    value={draft.roleId}
                    disabled={!canEdit || owner}
                    options={roleOptions}
                    onChange={(roleId) => {
                      edit({ roleId })
                    }}
                  />
                </div>
                <div className="ui-field">
                  <span className="ui-label" aria-hidden="true">
                    Аптека
                  </span>
                  <Select
                    label="Аптека"
                    value={draft.storeId}
                    disabled={!canEdit || owner}
                    options={storeOptions}
                    onChange={(storeId) => {
                      edit({ storeId })
                    }}
                  />
                </div>
              </div>
              <span className="ui-hint">
                {owner
                  ? 'У владельца роль и аптека не меняются'
                  : draft.storeId
                    ? 'Сотрудник видит заказы и остатки только этой аптеки'
                    : 'Сотрудник видит всю сеть'}
              </span>
            </div>
          </section>
        </div>

        <div className="ui-detail-col">{person ? <StaffSessions person={person} /> : null}</div>
      </div>

      {canEdit ? (
        <div className="ui-savebar" data-dirty={(edits !== null && dirty) || undefined}>
          <span className="state" role="status">
            {person === null
              ? ready
                ? 'Можно добавлять'
                : 'Заполните имя, телефон и роль'
              : edits !== null && dirty
                ? 'Есть несохранённые изменения'
                : 'Все изменения сохранены'}
          </span>
          {person === null || (edits !== null && dirty) ? (
            <button
              type="button"
              className="ui-btn"
              data-variant="secondary"
              disabled={save.isPending}
              onClick={() => {
                if (person) {
                  setEdits(null)
                  setError('')
                } else {
                  void navigate(listAddress('/admins'))
                }
              }}
            >
              Отменить
            </button>
          ) : null}
          <button
            type="button"
            className="ui-btn"
            disabled={!ready || !dirty || (person !== null && edits === null) || save.isPending}
            aria-busy={save.isPending}
            onClick={submit}
          >
            {person ? 'Сохранить' : 'Добавить сотрудника'}
          </button>
        </div>
      ) : null}

      {person && removing ? (
        <Modal
          title="Удалить сотрудника?"
          onClose={() => {
            setRemoving(false)
          }}
        >
          <p>
            {person.name} больше не войдёт в админку, его сеансы завершатся. Вернуть удалённого
            нельзя — только добавить заново.
          </p>
          <div className="mfoot">
            <button
              type="button"
              className="ui-btn"
              data-variant="secondary"
              onClick={() => {
                setRemoving(false)
              }}
            >
              Не удалять
            </button>
            <button
              type="button"
              className="ui-btn"
              data-variant="danger"
              disabled={remove.isPending}
              aria-busy={remove.isPending}
              onClick={() => {
                confirmRemove(person)
              }}
            >
              Удалить
            </button>
          </div>
        </Modal>
      ) : null}

      {issued ? (
        <IssuedPassword
          name={issued.name}
          password={issued.password}
          onClose={closeIssued}
          onToast={onToast}
        />
      ) : null}
    </>
  )
}

/** Можно ли сотруднику войти сейчас: отключён, закрыт после промахов или активен. */
export function StaffStatus({ person, now }: { person: Staff; now: number }) {
  if (!person.active) return <span className="ui-status">отключён</span>
  if (person.lockedUntil > now) {
    // Пять промахов паролем: вход закрыт на время, доступ не снят.
    return (
      <span className="ui-status" data-tone="danger">
        вход заблокирован до {formatTime(person.lockedUntil)}
      </span>
    )
  }
  return (
    <span className="ui-status" data-tone="success">
      активен
    </span>
  )
}
